<script setup lang="ts">
  import { computed } from 'vue';

  import type { Call, TaskCall } from '@stuut/api/models';
  import { TranscriptRole } from '@stuut/api/models';
  import { cn } from '@ui/utils/utils';

  interface Props {
    allowSegmentPlay?: boolean;
    audioTime?: number;
    call: Call | TaskCall;
    compact?: boolean;
    isSelected?: boolean;
  }

  const props = defineProps<Props>();

  const emits = defineEmits<{
    (e: 'playTranscript', transcript: Record<string, any>): void;
    (e: 'jumpTo', time: number): any;
  }>();

  const roundedAudioTime = computed(() => parseFloat((props.audioTime || 0).toFixed(2)));

  const toDisplayName = (role: TranscriptRole) => {
    switch (role) {
      case TranscriptRole.AGENT:
        return 'Agent';
      case TranscriptRole.TRANSFER_TARGET:
        return 'Support';
      case TranscriptRole.USER:
        return 'Customer';
      default:
        return 'Unknown';
    }
  };
</script>

<template>
  <div class="transition-colors duration-75" :class="compact ? 'space-y-2' : 'space-y-4'">
    <div v-for="(obj, idx) in props.call.transcript" :key="idx" class="flex flex-nowrap">
      <div
        class="w-[150px] min-w-[150px] group"
        :class="{
          'font-bold': obj.role === 'user',
          'text-muted-foreground': obj.role === 'agent' || obj.role === 'transfer_target',
          'cursor-pointer': props.allowSegmentPlay,
          italic: obj.role === 'transfer_target',
          'text-black':
            obj.words?.length &&
            roundedAudioTime >= 0 &&
            roundedAudioTime >= Math.max(obj.words[0].start - 0.25, 0) &&
            roundedAudioTime <= obj.words[obj.words?.length - 1].end - 0.5,
        }"
        @click="emits('playTranscript', obj)"
      >
        {{ toDisplayName(obj.role) }}

        <i
          v-if="allowSegmentPlay"
          class="fa-regular fa-solid fa-circle-play text-muted-foreground ml-2 hidden group-hover:inline"
        />
      </div>

      <div>
        <span
          v-for="(wordGroup, idx) in obj.words"
          :key="idx"
          :class="
            cn(
              'cursor-pointer',
              roundedAudioTime >= 0 &&
                roundedAudioTime >= wordGroup.start &&
                roundedAudioTime <= obj.words[obj.words?.length - 1].end &&
                'text-black bg-blue-100',
            )
          "
          @click="emits('jumpTo', wordGroup.start)"
        >
          {{ `${wordGroup.word} ` }}
        </span>
      </div>
    </div>
  </div>
</template>

<style scoped>
  .text-black .bg-highlight-muted {
    color: var(--blue);
  }
</style>
